import React from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import Header from '../../../commons/Header';
import { ReactComponent as IconBuilding } from '../../../images/icn_buildings.svg';
import { Col, CustomInput, FormGroup, Label, Row } from 'reactstrap';
import {
  ContFilterYear,
  ContFlexRight,
  ContPaginationPage,
} from '../../../styled-components';
import generateYearOptions from '../../../lib/helpers/generateYearOptions';
import generateMonthOptions from '../../../lib/helpers/generateMonthOptions';
import SearchInput from '../../../commons/SearchInput';
import generatePaginationOptions from '../../../lib/helpers/generatePaginationOptions';
import Footer from '../../../commons/Footer';
import TotalTr from './TotalTr';
import ClaimsTable from './ClaimsTable';

const ClaimsPresentational = ({
  data,
  events,
  profile,
  query,
  total,
  onFilter,
  searchSuppliers,
  onClaimUpdate,
  onClaimDelete,
}) => {
  const [t] = useTranslation('CLAIMS');
  const alert = useAlert();
  const { offset, limit, status, event, intern } = query;

  const getTotals = (data) => {
    const reserveTotals = data.reduce(
      (prev, cur) => prev + (cur.reserve || 0),
      0
    );
    const amountTotals = data.reduce(
      (prev, cur) => prev + (cur.paid_amount || 0),
      0
    );

    return {
      reserveTotals,
      amountTotals,
    };
  };

  return (
    <section>
      <Header
        title={t('PAGES:CLAIMS')}
        icon={<IconBuilding fill="#ffffff" style={{ height: '25px' }} />}
      />

      <Row className="align-items-center">
        <Col className="">
          <ContFlexRight>
            Sinistri:&nbsp;<strong>{total}</strong>&nbsp;
          </ContFlexRight>
        </Col>
      </Row>

      <Row className="search-add-cont m-t-20" style={{ rowGap: '1rem' }}>
        <Col sm="12" md="6" lg="3" className="select-wrapper">
          <ContFilterYear className="no-width" style={{ marginBottom: '0' }}>
            <Row>
              <Col xs="6">
                <Label>{t('CLAIM_DATE_YEAR')}</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="selectClaimStartYear"
                    name="claimStartYear"
                    value={query && query.claimStartYear}
                    onChange={({ target: { value: claimStartYear } }) => {
                      onFilter({ claimStartYear });
                    }}
                  >
                    {generateYearOptions(true)}
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
        </Col>

        <Col sm="12" md="6" lg="3" className="select-wrapper">
          <ContFilterYear className="no-width" style={{ marginBottom: '0' }}>
            <Row>
              <Col xs="6">
                <Label>{t('CLAIM_DATE_MONTH')}</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="claimStartMonth"
                    name="claimStartMonth"
                    disabled={!query.claimStartYear}
                    value={query && query.claimStartMonth}
                    onChange={({ target: { value: claimStartMonth } }) =>
                      onFilter({ claimStartMonth })
                    }
                  >
                    {generateMonthOptions()}
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
        </Col>

        <Col sm="12" md="6" lg="3" xl="3" className="select-wrapper">
          <ContFilterYear className="no-width" style={{ marginBottom: '0' }}>
            <Row>
              <Col xs="6">
                <Label>Esito sinistro</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="status"
                    name="status"
                    value={status}
                    onChange={({ target: { value: status } }) =>
                      onFilter({ status })
                    }
                  >
                    <option value="" defaultChecked>
                      Tutti
                    </option>
                    <option value="CHECK">Verifica</option>
                    <option value="SETTLED">Liquidato</option>
                    <option value="CLOSED">Chiuso senza seguito</option>
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
        </Col>

        <Col sm="12" md="6" lg="3" xl="3" className="select-wrapper">
          <ContFilterYear className="no-width" style={{ marginBottom: '0' }}>
            <Row>
              <Col xs="6">
                <Label>Evento sinistro</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="event"
                    name="event"
                    value={event}
                    onChange={({ target: { value: event } }) =>
                      onFilter({ event })
                    }
                  >
                    <option value="" defaultChecked>
                      Tutti
                    </option>
                    {events.length > 0 &&
                      events?.map((event) => (
                        <option key={event._id} value={event._id}>
                          {`${event.name}`}
                        </option>
                      ))}
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
        </Col>

        <Col sm="12" md="6" lg="3" xl="3" className="select-wrapper">
          <ContFilterYear className="no-width" style={{ marginBottom: '0' }}>
            <Row>
              <Col xs="6">
                <Label>Tipo fornitore incaricato</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="intern"
                    name="intern"
                    value={intern}
                    onChange={({ target: { value: intern } }) =>
                      onFilter({ intern })
                    }
                  >
                    <option value="" defaultChecked>
                      Tutti
                    </option>
                    <option value="INTERN">Interno</option>
                    <option value="NOT_INTERN">Esterno</option>
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
        </Col>

        <Col sm="6" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.branch }}
            onFilter={({ search }) => onFilter({ branch: search })}
            label={t('SEARCH:BRANCH')}
          />
        </Col>

        <Col sm="6" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.policy_code }}
            onFilter={({ search }) => onFilter({ policy_code: search })}
            label={t('SEARCH:POLICY_CODE')}
          />
        </Col>

        <Col sm="6" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.insurance_code }}
            onFilter={({ search }) => onFilter({ insurance_code: search })}
            label={t('SEARCH:INSURANCE_CODE')}
          />
        </Col>

        <Col sm="6" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.admin }}
            onFilter={({ search }) => onFilter({ admin: search })}
            label={t('SEARCH:REFERENCE_ID')}
          />
        </Col>

        <Col sm="6" md="6" lg="3" xl="3">
          <SearchInput
            query={{ search: query && query.appointedSupplier }}
            onFilter={({ search }) => onFilter({ appointedSupplier: search })}
            label={t('SEARCH:APPOINTED_SUPPLIER')}
          />
        </Col>

        {profile?.type !== 'SUPPLIER_INS' && (
          <Col sm="6" md="6" lg="3" xl="3">
            <SearchInput
              query={{ search: query && query.supplier }}
              onFilter={({ search }) => onFilter({ supplier: search })}
              label={t('SEARCH:BY_SUPPLIER')}
            />
          </Col>
        )}
      </Row>

      <section className="m-t-20">
        <ClaimsTable
          data={data}
          total={total}
          offset={offset}
          limit={limit}
          onFilter={onFilter}
          profile={profile}
          searchSuppliers={searchSuppliers}
          onClaimUpdate={onClaimUpdate}
          onClaimDelete={onClaimDelete}
          events={events}
          bottomRow={
            <TotalTr profileType={profile.type} totals={getTotals(data)} />
          }
        />

        <ContPaginationPage>
          <p>{t('COMMON:PAGINATION_PER_PAGE')}</p>

          <FormGroup>
            <CustomInput
              type="select"
              className="pagination-select"
              id="perPage"
              name="perPage"
              value={limit}
              onChange={({ target: { value: limit } }) => {
                onFilter({ limit: limit });
              }}
            >
              {generatePaginationOptions()}
            </CustomInput>
          </FormGroup>
        </ContPaginationPage>
      </section>
      <Footer />
    </section>
  );
};

export default ClaimsPresentational;
